// Single source of truth for all in-app help. Edit here to regenerate/update help
// after system changes — every surface (top-bar panel, coach tips, Help centre) reads from this file.
export const HELP_VERSION = "2026.06";
export const HELP_UPDATED = "June 2026";

const ALL = ["admin", "hr_admin", "manager", "employee"];

// Contextual help shown in the top-bar Help panel + first-visit coach tips, keyed by route prefix.
export const PAGE_HELP = {
  "/": {
    title: "Home",
    intro: "Your personalised dashboard. It adapts to your role and highlights what needs your attention.",
    tips: [
      "The banner shows company news, values and the latest announcements in 'What's new'.",
      "Cards below surface your leave balance, tasks to action and quick shortcuts.",
      "A blue dot on the Home menu item means there's a fresh announcement to read.",
    ],
  },
  "/my-hr": {
    title: "My Profile",
    intro: "Everything about you in one place — your details, leave overview, employment journey and documents.",
    tips: [
      "Use 'Edit my details' to update your contact details and profile photo.",
      "The overview tiles show your holiday balance and length of service at a glance.",
      "'To acknowledge' lists any policies you still need to confirm you've read.",
    ],
  },
  "/requests": {
    title: "Leave & Absence",
    intro: "Book time off and keep track of your allowance. Your balance updates automatically.",
    tips: [
      "Click 'New request', pick your dates and leave type — the system calculates the exact deduction for you.",
      "Half-days and specific hours are supported; choose the duration when booking.",
      "Requests go to your manager for approval — you'll be notified when they're actioned.",
    ],
  },
  "/my-total-reward": {
    title: "Benefits (Total Reward)",
    intro: "See the full value of your pay and benefits, not just your salary.",
    tips: [
      "Your indicative total reward combines salary, cash allowances, employer-funded benefits and extra paid leave.",
      "Export a PDF or Excel copy any time using the buttons in the header.",
      "If your statement isn't visible yet, it simply hasn't been published by HR — check back soon.",
    ],
  },
  "/learning": {
    title: "Learning & Development",
    intro: "Track your training, keep certifications current and discover new courses.",
    tips: [
      "'My Learning' shows your active and renewal-due courses; 'History' is your full record.",
      "Browse 'Available' and click 'Register interest' — HR will follow up to arrange enrolment.",
      "Mandatory courses are flagged so you know what you must complete.",
    ],
  },
  "/wellbeing": {
    title: "Wellbeing",
    intro: "Confidential support, healthy habits and a quick private check-in.",
    tips: [
      "Your daily mood check-in is completely private — only anonymous team trends are shared with HR.",
      "The EAP helpline is free, confidential and available 24/7 for you and your family.",
      "Explore the resource cards for mental health, financial and physical wellbeing support.",
    ],
  },
  "/directory": {
    title: "People Directory",
    intro: "Find colleagues across CASS.",
    tips: ["Search by name, team or department.", "Click a person to see their public profile card."],
  },
  "/calendar": {
    title: "Team Calendar",
    intro: "See who's off and when, so you can plan around your team.",
    tips: ["Approved leave and bank holidays are shown.", "Use it before booking your own time off to avoid clashes."],
  },
  // ---- Manager / HR / Admin ----
  "/employees": {
    title: "People",
    intro: "Your directory of colleagues with balances and records.",
    roles: ["admin", "hr_admin", "manager"],
    tips: [
      "Search by name, code or department to find a record fast.",
      "Admins can add a new employee or bulk-import from a spreadsheet.",
      "Open a record to manage details, leave, benefits, training and documents.",
    ],
  },
  "/approvals": {
    title: "Approvals",
    intro: "Review and action leave requests from your team.",
    roles: ["admin", "hr_admin", "manager"],
    tips: ["Approve or decline with an optional note.", "The employee is notified automatically of your decision."],
  },
  "/hr-hub": {
    title: "HR Hub",
    intro: "A single view of everything needing HR attention across your people.",
    roles: ["admin", "hr_admin", "manager"],
    tips: [
      "Each tile is clickable — open it to see the underlying list.",
      "Admins can 'Publish all statements' here to release Total Reward to everyone at once.",
    ],
  },
  "/people/cases": {
    title: "HR Cases",
    intro: "Confidential grievance, disciplinary and absence case management.",
    roles: ["admin", "hr_admin", "manager"],
    tips: ["Open a case, record notes and track its status through to closure.", "Access is restricted and audit-logged."],
  },
  "/recruitment": {
    title: "Recruitment",
    intro: "Manage vacancies from approval through to hiring.",
    roles: ["admin", "hr_admin", "manager"],
    tips: [
      "Create a vacancy; it moves through approval before going live.",
      "Open a vacancy's candidates to manage the pipeline and copy the public application link.",
    ],
  },
  "/reports": {
    title: "Reports",
    intro: "View, filter and export HR information across CASS.",
    roles: ["admin", "hr_admin", "manager"],
    tips: ["Search or filter by group to find a report.", "Star your favourites to pin them to the top."],
  },
  "/settings": {
    title: "Settings",
    intro: "Configure the platform — leave years, reward, homepage content, the learning catalogue and more.",
    roles: ["admin"],
    tips: [
      "Homepage content lets you set the hero image, company logo, values and announcements.",
      "The Benefit catalogue supports bulk-assigning a benefit to a team, department or everyone.",
      "Changes are saved per section — remember to click Save.",
    ],
  },
};

// Full Help-centre articles. `roles` gates who sees each one.
export const HELP_CATEGORIES = [
  "Getting started", "Leave & absence", "Benefits", "Learning", "Wellbeing",
  "People & records", "Recruitment", "Reports & admin",
];

export const HELP_ARTICLES = [
  {
    id: "welcome", category: "Getting started", roles: ALL, icon: "Rocket",
    title: "Welcome to the CASS HR portal",
    summary: "A quick tour of how the portal is organised and how to get around.",
    body: [
      ["Finding your way", "Use the left sidebar to move between areas. The menu adapts to your role, so you only ever see what's relevant to you."],
      ["Search anything", "The search bar at the top finds people, documents and reports you have permission to see."],
      ["Getting help", "Click the ? Help button in the top bar on any page for tips about that screen, or come here for the full guide."],
      ["Notifications", "The bell shows updates such as approved leave or new tasks. A blue dot on Home means there's a new announcement."],
    ],
  },
  {
    id: "profile", category: "Getting started", roles: ALL, icon: "UserCircle",
    title: "Updating your profile & photo",
    summary: "Keep your contact details and photo up to date.",
    body: [
      ["Edit my details", "Open My Profile and click 'Edit my details' to update your phone, address and emergency contact."],
      ["Profile photo", "Upload a photo from the same screen — it appears across the directory and your profile."],
    ],
  },
  {
    id: "book-leave", category: "Leave & absence", roles: ALL, icon: "CalendarDays",
    title: "Booking time off",
    summary: "How to request leave and understand your balance.",
    body: [
      ["Make a request", "Go to Leave & Absence → New request, choose your dates and leave type, then submit. The system calculates the exact deduction, including part-days and specific hours."],
      ["Your balance", "The banner shows your allowance, what you've taken or booked, and what's left. It updates as requests are approved."],
      ["Approvals", "Requests go to your manager. You'll be notified when they're approved or declined."],
    ],
  },
  {
    id: "leave-approve", category: "Leave & absence", roles: ["admin", "hr_admin", "manager"], icon: "CheckCircle2",
    title: "Approving your team's leave",
    summary: "Review and action requests, and plan around cover.",
    body: [
      ["Review requests", "Open Approvals to see pending requests. Approve or decline with an optional note."],
      ["Check the calendar", "Use the Team Calendar to see who else is off before approving, to avoid leaving a team short."],
    ],
  },
  {
    id: "total-reward", category: "Benefits", roles: ALL, icon: "Gift",
    title: "Understanding your Total Reward",
    summary: "The full value of your pay and benefits explained.",
    body: [
      ["What's included", "Your statement combines basic salary, cash allowances, employer-funded benefits and the value of additional paid leave."],
      ["Exporting", "Download a PDF or Excel copy any time from the Benefits page."],
      ["Not visible yet?", "Statements are released by HR. If yours isn't showing, it simply hasn't been published yet."],
    ],
  },
  {
    id: "benefits-admin", category: "Benefits", roles: ["admin", "hr_admin"], icon: "Layers",
    title: "Managing benefits & publishing statements",
    summary: "Assign benefits and release Total Reward statements.",
    body: [
      ["Benefit catalogue", "In Settings, maintain the catalogue of benefits. Use 'Bulk assign' to give a benefit to a whole department, a manager's team or everyone at once."],
      ["Publishing", "Publish an individual statement from an employee's Total Reward tab, or use 'Publish all statements' on the HR Hub to release everyone's at once."],
    ],
  },
  {
    id: "learning", category: "Learning", roles: ALL, icon: "GraduationCap",
    title: "Growing your skills",
    summary: "Track training and find new courses.",
    body: [
      ["My Learning & History", "'My Learning' shows current and renewal-due courses; 'History' is your complete record with completion and expiry dates."],
      ["Registering interest", "Browse 'Available' and click 'Register interest' on any course — HR is notified and will help arrange it."],
    ],
  },
  {
    id: "learning-admin", category: "Learning", roles: ["admin", "hr_admin"], icon: "BookOpen",
    title: "Managing the learning catalogue",
    summary: "Add, edit and retire the courses employees can see.",
    body: [
      ["Editing courses", "Settings → Learning catalogue lets you add courses, mark them mandatory, and toggle them active/inactive without deleting."],
    ],
  },
  {
    id: "wellbeing", category: "Wellbeing", roles: ALL, icon: "HeartPulse",
    title: "Looking after your wellbeing",
    summary: "Confidential support and a private daily check-in.",
    body: [
      ["Daily check-in", "Log how you're feeling in seconds. Your individual answer is private — HR only ever sees an anonymous team trend, and only once enough people have checked in."],
      ["Confidential support", "The Employee Assistance Programme helpline is free and available 24/7 for you and your family."],
    ],
  },
  {
    id: "wellbeing-trend", category: "Wellbeing", roles: ["admin", "hr_admin"], icon: "BarChart3",
    title: "Reading the wellbeing trend",
    summary: "How the anonymous team trend works.",
    body: [
      ["Privacy first", "The trend is aggregated and anonymous. It only appears once at least a handful of check-ins exist, so no individual can be identified."],
    ],
  },
  {
    id: "people", category: "People & records", roles: ["admin", "hr_admin", "manager"], icon: "Users",
    title: "Working with people records",
    summary: "Find and manage employee records.",
    body: [
      ["Finding records", "Use People and search by name, code or department. Managers see their team; HR and admins see everyone."],
      ["Inside a record", "Tabs cover details, leave, benefits, training, documents, performance and (for admins) job history."],
    ],
  },
  {
    id: "cases", category: "People & records", roles: ["admin", "hr_admin", "manager"], icon: "ShieldCheck",
    title: "Handling HR cases",
    summary: "Confidential case management.",
    body: [["Opening a case", "Record grievance, disciplinary or absence cases, add notes and move them through to closure. Access is restricted and every change is audit-logged."]],
  },
  {
    id: "recruitment", category: "Recruitment", roles: ["admin", "hr_admin", "manager"], icon: "Briefcase",
    title: "Running a vacancy",
    summary: "From approval to hire.",
    body: [
      ["Create & approve", "Add a vacancy; it moves through approval before going live."],
      ["Candidate pipeline", "Open a vacancy's candidates to move applicants through stages. Copy the public application link to share the role."],
    ],
  },
  {
    id: "reports", category: "Reports & admin", roles: ["admin", "hr_admin", "manager"], icon: "BarChart3",
    title: "Finding & exporting reports",
    summary: "Get the data you need.",
    body: [["Using the Report Hub", "Search or filter reports by group, star your favourites, and export where available."]],
  },
  {
    id: "homepage-content", category: "Reports & admin", roles: ["admin"], icon: "Image",
    title: "Branding & homepage content",
    summary: "Logo, hero image, values and announcements.",
    body: [
      ["Logo & hero", "Settings → Homepage content lets you upload your company logo (shown in the sidebar and sign-in) and a hero background image."],
      ["Announcements", "Add announcements that appear in the homepage 'What's new' card. Staff get a subtle 'new' indicator until they've read them."],
    ],
  },
];

export const iconForCategory = {
  "Getting started": "Rocket", "Leave & absence": "CalendarDays", "Benefits": "Gift",
  "Learning": "GraduationCap", "Wellbeing": "HeartPulse", "People & records": "Users",
  "Recruitment": "Briefcase", "Reports & admin": "BarChart3",
};

export function pageHelpFor(pathname) {
  if (PAGE_HELP[pathname]) return PAGE_HELP[pathname];
  // longest matching prefix (e.g. /recruitment/:id -> /recruitment)
  const key = Object.keys(PAGE_HELP)
    .filter((k) => k !== "/" && pathname.startsWith(k))
    .sort((a, b) => b.length - a.length)[0];
  return key ? PAGE_HELP[key] : null;
}

export function visibleArticles(role) {
  return HELP_ARTICLES.filter((a) => a.roles.includes(role));
}
